<template>
  <div>
    <Head title="Laporan Jenis Beasiswa" />

    <!-- FILTER -->
    <div class="w-full mb-8 bg-white rounded-md shadow">
      <div class="w-full p-4 font-semibold bg-gray-200">Filter</div>
      <div class="flex flex-wrap w-full gap-4 p-6">
        <div class="flex flex-wrap w-full lg:w-6/12 items-center gap-2">
          <div class="w-full font-semibold lg:w-4/12">Periode:</div>
          <v-select
            v-model="form.periode"
            :options="periodes"
            class="w-full lg:w-8/12"
            :reduce="p => p.id"
            label="nama"
            placeholder="Pilih Periode"
          />
        </div>
        <div class="flex flex-wrap w-full lg:w-5/12 items-center gap-2">
          <div class="w-full font-semibold lg:w-4/12">Jenis Beasiswa:</div>
          <v-select
            v-model="form.jenis_beasiswa"
            :options="jenisOptions"
            class="w-full lg:w-8/12"
            :reduce="o => o.value"
            label="label"
            placeholder="Semua Jenis"
          />
        </div>
      </div>
    </div>

    <!-- RINGKASAN TOTAL -->
    <div class="w-full p-4 mb-6 bg-white border rounded shadow">
      <h2 class="mb-3 text-lg font-semibold">Ringkasan Penerima Beasiswa</h2>
      <div class="flex flex-wrap gap-4 text-sm">
        <div class="flex items-center gap-2 px-4 py-2 rounded text-white font-semibold" style="background:#3b82f6">
          Total: {{ total }}
        </div>
        <div class="flex items-center gap-2 px-4 py-2 rounded text-white font-semibold" style="background:#8b5cf6">
          Prestasi: {{ prestasi }}
        </div>
        <div class="flex items-center gap-2 px-4 py-2 rounded text-white font-semibold" style="background:#f97316">
          Prasejahtera: {{ prasejahtera }}
        </div>
        <div v-if="lainnya > 0" class="flex items-center gap-2 px-4 py-2 rounded text-white font-semibold" style="background:#6b7280">
          Lainnya: {{ lainnya }}
        </div>
      </div>
    </div>

    <!-- TOMBOL EXPORT -->
    <div class="flex justify-end mb-4">
      <a
        :href="exportUrl"
        class="px-4 py-2 text-sm font-semibold text-white rounded shadow bg-green-600 hover:bg-green-700"
      >
        ⬇ Download Excel
      </a>
    </div>

    <!-- TAB NAVIGASI -->
    <div class="flex mb-0 border-b border-gray-200">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="px-5 py-2 text-sm font-semibold border-b-2 transition-colors"
        :class="activeTab === tab.key
          ? 'border-indigo-600 text-indigo-600'
          : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <!-- TAB: WILAYAH -->
    <div v-if="activeTab === 'wilayah'" class="overflow-auto bg-white border border-t-0 rounded-b shadow mb-10">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-100 border-b">
            <th class="p-2 text-left border">Kecamatan</th>
            <th class="p-2 text-center border">Total</th>
            <th class="p-2 text-center border" style="color:#8b5cf6">Prestasi</th>
            <th class="p-2 text-center border" style="color:#f97316">Prasejahtera</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in per_wilayah" :key="i" class="border-b hover:bg-gray-50">
            <td class="p-2 font-semibold border">{{ item.kecamatan }}</td>
            <td class="p-2 text-center border font-semibold">{{ item.total }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#8b5cf6">{{ item.prestasi }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#f97316">{{ item.prasejahtera }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- TAB: PERGURUAN TINGGI -->
    <div v-if="activeTab === 'universitas'" class="overflow-auto bg-white border border-t-0 rounded-b shadow mb-10">
      <div class="flex justify-end p-3 border-b bg-gray-50">
        <a
          :href="exportUrlUniversitas"
          class="px-4 py-2 text-sm font-semibold text-white rounded shadow bg-green-600 hover:bg-green-700"
        >
          ⬇ Download Excel Perguruan Tinggi
        </a>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-100 border-b">
            <th class="w-12 p-2 text-center border">No</th>
            <th class="p-2 text-left border">Nama Kampus</th>
            <th class="p-2 text-center border">Total</th>
            <th class="p-2 text-center border" style="color:#8b5cf6">Prestasi</th>
            <th class="p-2 text-center border" style="color:#f97316">Prasejahtera</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in per_universitas" :key="i" class="border-b hover:bg-gray-50">
            <td class="p-2 text-center border">{{ i + 1 }}</td>
            <td class="p-2 font-semibold border">{{ item.universitas }}</td>
            <td class="p-2 text-center border font-semibold">{{ item.total }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#8b5cf6">{{ item.prestasi }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#f97316">{{ item.prasejahtera }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- TAB: PROGRAM STUDI -->
    <div v-if="activeTab === 'prodi'" class="overflow-auto bg-white border border-t-0 rounded-b shadow mb-10">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-100 border-b">
            <th class="w-12 p-2 text-center border">No</th>
            <th class="p-2 text-left border">Program Studi</th>
            <th class="p-2 text-left border">Kampus</th>
            <th class="p-2 text-center border">Total</th>
            <th class="p-2 text-center border" style="color:#8b5cf6">Prestasi</th>
            <th class="p-2 text-center border" style="color:#f97316">Prasejahtera</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in per_prodi" :key="i" class="border-b hover:bg-gray-50">
            <td class="p-2 text-center border">{{ i + 1 }}</td>
            <td class="p-2 font-semibold border">{{ item.prodi }}</td>
            <td class="p-2 border">{{ item.universitas }}</td>
            <td class="p-2 text-center border font-semibold">{{ item.total }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#8b5cf6">{{ item.prestasi }}</td>
            <td class="p-2 text-center border font-semibold" style="color:#f97316">{{ item.prasejahtera }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- CHART -->
    <div class="p-4 bg-white rounded shadow">
      <h2 class="mb-4 text-xl font-bold">Grafik Jenis Beasiswa</h2>
      <apexchart
        width="100%"
        height="420"
        type="bar"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import VueApexCharts from 'vue3-apexcharts'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'

export default {
  components: {
    Head,
    apexchart: VueApexCharts,
  },

  layout: Layout,

  props: {
    filters:         Object,
    periodes:        Array,
    total:           Number,
    prestasi:        Number,
    prasejahtera:    Number,
    lainnya:         Number,
    per_wilayah:     Array,
    per_universitas: Array,
    per_prodi:       Array,
  },

  data() {
    return {
      activeTab: 'wilayah',
      tabs: [
        { key: 'wilayah',      label: 'Wilayah' },
        { key: 'universitas',  label: 'Perguruan Tinggi' },
        { key: 'prodi',        label: 'Program Studi' },
      ],
      jenisOptions: [
        { label: 'Prestasi',     value: 'PRESTASI' },
        { label: 'Prasejahtera', value: 'TIDAK MAMPU' },
      ],
      form: {
        periode:        parseInt(this.filters.periode) || null,
        jenis_beasiswa: this.filters.jenis_beasiswa || null,
      },
    }
  },

  computed: {
    exportUrl() {
      const params = new URLSearchParams(pickBy({
        periode:        this.form.periode,
        jenis_beasiswa: this.form.jenis_beasiswa,
        tab:            this.activeTab,
      }))
      return this.route('report.hasil_verifikasi.export') + '?' + params.toString()
    },

    exportUrlUniversitas() {
      const params = new URLSearchParams(pickBy({
        periode: this.form.periode,
        jenis_beasiswa: this.form.jenis_beasiswa,
        tab: 'universitas',
      }))
      return this.route('report.hasil_verifikasi.export') + '?' + params.toString()
    },

    chartLabels() {
      if (this.activeTab === 'universitas') return this.per_universitas.map(r => r.universitas)
      if (this.activeTab === 'prodi')       return this.per_prodi.map(r => r.prodi)
      return this.per_wilayah.map(r => r.kecamatan)
    },

    chartData() {
      const source = this.activeTab === 'universitas'
        ? this.per_universitas
        : this.activeTab === 'prodi'
          ? this.per_prodi
          : this.per_wilayah

      return source
    },

    chartSeries() {
      return [
        { name: 'Prestasi',     data: this.chartData.map(r => r.prestasi) },
        { name: 'Prasejahtera', data: this.chartData.map(r => r.prasejahtera) },
      ]
    },

    chartOptions() {
      return {
        chart: { toolbar: { show: false }, stacked: false },
        xaxis: {
          categories: this.chartLabels,
          labels: { rotate: -45 },
        },
        dataLabels: { enabled: true },
        colors: ['#8b5cf6', '#f97316'],
        legend: { position: 'top' },
      }
    },
  },

  watch: {
    form: {
      handler: throttle(function () {
        this.$inertia.get(
          this.route('report.hasil_verifikasi'),
          pickBy(this.form),
          { preserveState: true, preserveScroll: true }
        )
      }, 150),
      deep: true,
    },
  },
}
</script>
